import React, { memo, useEffect, useMemo, useState } from 'react';
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, TooltipProps } from 'recharts';
import dayjs from 'dayjs';
import '@/assets/styles/components/statistics-chart.styles.scss';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface ChartDataItem {
	value?: number;
	value2?: number;
	value3?: number;
	info?: string;
	sessionDate?: string;
	[key: string]: any;
}

export interface StatisticsChartProps {
	unit?: string;
	type?: 'line' | 'bar' | 'mixedBar';
	data?: ChartDataItem[];
	endDate?: string;
	onDateChange?: (dates: { start: string; end: string }) => void;
	period?: 'DAILY' | 'WEEKLY' | 'MONTHLY';
	tickCount?: number;
	barColors?: string[];
	skipZeroValue?: boolean;
	noDataMessage?: string;
	noDataImage?: string;
	className?: string;
	height?: number;
	showNavigationButtons?: boolean;
}

interface GraphicDataItem extends ChartDataItem {
	name: string;
}

const PERIOD_MAP = {
	DAILY: 'day',
	WEEKLY: 'week',
	MONTHLY: 'month'
} as const;

const DEFAULT_PERIOD_AMOUNTS = {
	DAILY: { display: 8, navigation: 9 },
	WEEKLY: { display: 3, navigation: 4 },
	MONTHLY: { display: 8, navigation: 9 }
};

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload }) => {
	if (active && payload && payload.length && payload[0].payload.info) {
		return (
			<div className="statistics-chart__tooltip">
				<span className="statistics-chart__tooltip-info">{payload[0].payload.info}</span>
			</div>
		);
	}
	return null;
};

export const StatisticsChart = memo<StatisticsChartProps>(
	({
		unit = '',
		type = 'line',
		data = [],
		endDate = dayjs().format(),
		onDateChange,
		period = 'MONTHLY',
		tickCount = 5,
		barColors = ['var(--surface-e0e7ea)', 'var(--surface-f85050)', '#1abc9d'],
		skipZeroValue = false,
		noDataMessage = 'Gösterilecek veri bulunamadı',
		noDataImage,
		className = '',
		height = 300,
		showNavigationButtons = true
	}) => {
		const [graphicData, setGraphicData] = useState<GraphicDataItem[]>([]);
		const nowDate = useMemo(() => dayjs().format(), []);
		const [currentPeriod, setCurrentPeriod] = useState(period);

		const currentEndDate = useMemo(() => endDate, [endDate]);

		const periodAmounts = useMemo(() => {
			const amounts = DEFAULT_PERIOD_AMOUNTS[period];
			if (skipZeroValue && period === 'DAILY') {
				return { display: 10, navigation: 10 };
			}
			return amounts;
		}, [period, skipZeroValue]);

		const canNavigateNext = useMemo(() => {
			return dayjs(endDate).isBefore(dayjs(nowDate), 'day');
		}, [endDate, nowDate]);

		const handlePrevious = () => {
			if (!onDateChange) return;

			let prevAmount = 9;
			let nextAmount = 17;

			if (period === 'WEEKLY') {
				prevAmount = 4;
				nextAmount = 7;
			}

			onDateChange({
				end: dayjs(endDate).subtract(prevAmount, PERIOD_MAP[period]).format(),
				start: dayjs(endDate).subtract(nextAmount, PERIOD_MAP[period]).format()
			});
		};

		const handleNext = () => {
			if (!onDateChange || !canNavigateNext) return;

			let nextAmount = 9;

			if (period === 'WEEKLY') {
				nextAmount = 4;
			}

			onDateChange({
				end: dayjs(endDate).add(nextAmount, PERIOD_MAP[period]).format(),
				start: dayjs(endDate).add(1, PERIOD_MAP[period]).format()
			});
		};

		useEffect(() => {
			if (!data || !period) return;

			const { display } = periodAmounts;
			const processedData: GraphicDataItem[] = [];

			for (let x = display; x >= 0; x--) {
				let dataPoint: GraphicDataItem | null = null;

				if (period === 'DAILY') {
					const targetDate = dayjs(currentEndDate).subtract(x, 'day');
					const found = data.find(item => item.sessionDate && dayjs(item.sessionDate).isSame(targetDate, 'day'));

					dataPoint = {
						...found,
						value: found?.value || 0,
						info: found?.info || '',
						name: targetDate.format('DD MMM')
					};
				} else if (period === 'WEEKLY') {
					const weekEnd = dayjs(currentEndDate).subtract(x, 'weeks');
					const weekStart = weekEnd.subtract(6, 'days');

					const found = data.find(item => {
						if (!item.sessionDate) return false;
						const itemDate = dayjs(item.sessionDate);
						return itemDate.isSameOrAfter(weekStart, 'day') && itemDate.isSameOrBefore(weekEnd, 'day');
					});

					dataPoint = {
						...found,
						value: found?.value || 0,
						info: found?.info || '',
						name: `${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM')}`
					};
				} else if (period === 'MONTHLY') {
					const targetMonth = dayjs(currentEndDate).subtract(x, 'month');
					const found = data.find(item => item.sessionDate && dayjs(item.sessionDate).isSame(targetMonth, 'month'));

					dataPoint = {
						...found,
						value: found?.value || 0,
						info: found?.info || '',
						name: targetMonth.format('MMM')
					};
				}

				if (dataPoint && (!skipZeroValue || dataPoint.value !== 0)) {
					processedData.push(dataPoint);
				}
			}

			setGraphicData(processedData);

			if (onDateChange) {
				onDateChange({
					end: dayjs(endDate).format(),
					start: dayjs(endDate).subtract(display, PERIOD_MAP[period]).format()
				});
			}
		}, [data, period, currentEndDate, skipZeroValue, periodAmounts]);

		useEffect(() => {
			if (period !== currentPeriod) {
				const { display } = periodAmounts;

				if (onDateChange) {
					onDateChange({
						end: dayjs(nowDate).format(),
						start: dayjs(nowDate).subtract(display, PERIOD_MAP[period]).format()
					});
				}

				setCurrentPeriod(period);
			}
		}, [period, currentPeriod, nowDate, periodAmounts, onDateChange]);

		const renderChart = () => {
			switch (type) {
				case 'line':
					return (
						<AreaChart
							data={graphicData}
							margin={{ top: 20, right: 20, bottom: 5, left: 5 }}>
							<defs>
								<linearGradient
									id="areaGradient"
									x1="0"
									y1="0"
									x2="0"
									y2="1">
									<stop
										offset="10%"
										className="statistics-chart__gradient-start"
									/>
									<stop
										offset="90%"
										className="statistics-chart__gradient-end"
									/>
								</linearGradient>
							</defs>
							<CartesianGrid
								strokeDasharray="10"
								vertical={false}
							/>
							<XAxis
								dataKey="name"
								height={40}
								tickSize={20}
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								width={50}
								unit={unit ? `\u00a0${unit}` : ''}
								allowDecimals={false}
								tickLine={false}
								axisLine={false}
								tickCount={tickCount}
							/>
							<Tooltip content={<CustomTooltip />} />
							<Area
								type="monotone"
								dataKey="value"
								stroke="#6fe2cb"
								strokeWidth={2}
								fillOpacity={0.5}
								fill="url(#areaGradient)"
								activeDot={{ r: 6 }}
								dot={false}
							/>
						</AreaChart>
					);

				case 'bar':
					if (graphicData.length === 0 && noDataImage) {
						return (
							<div className="statistics-chart__no-data">
								<img
									src={noDataImage}
									alt="No data"
								/>
								<div className="statistics-chart__no-data-message">{noDataMessage}</div>
							</div>
						);
					}

					return (
						<BarChart
							data={graphicData}
							margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
							<CartesianGrid
								strokeDasharray="10"
								vertical={false}
							/>
							<XAxis
								dataKey="name"
								height={40}
								tickSize={20}
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								allowDecimals={false}
								axisLine={false}
								tickLine={false}
								tickCount={tickCount}
							/>
							<Tooltip
								cursor={{ fill: 'rgba(0,0,0,.05)', strokeWidth: 0 }}
								content={<CustomTooltip />}
							/>
							<Bar
								dataKey="value"
								fill={barColors[0]}
								barSize={30}
							/>
						</BarChart>
					);

				case 'mixedBar':
					return (
						<BarChart
							data={graphicData}
							margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
							<CartesianGrid
								strokeDasharray="10"
								vertical={false}
							/>
							<XAxis
								dataKey="name"
								height={40}
								tickSize={20}
								tickLine={false}
								axisLine={false}
							/>
							<YAxis
								allowDecimals={false}
								axisLine={false}
								tickLine={false}
								tickCount={tickCount}
							/>
							<Tooltip
								cursor={{ fill: 'rgba(0,0,0,.05)', strokeWidth: 0 }}
								content={<CustomTooltip />}
							/>
							<Bar
								dataKey="value3"
								stackId="a"
								fill={barColors[2]}
								barSize={50}
							/>
							<Bar
								dataKey="value2"
								stackId="a"
								fill={barColors[1]}
								barSize={50}
							/>
							<Bar
								dataKey="value"
								stackId="a"
								fill={barColors[0]}
								barSize={50}
							/>
						</BarChart>
					);

				default:
					return <></>;
			}
		};

		return (
			<div className={`statistics-chart statistics-chart--${type} ${className}`}>
				<div
					className="statistics-chart__container"
					style={{ height }}>
					{showNavigationButtons && period !== 'MONTHLY' && (
						<>
							<button
								className="statistics-chart__nav statistics-chart__nav--prev"
								onClick={handlePrevious}
								aria-label="Previous period">
								<i className="icons-alt-arrow-left" />
							</button>
							{canNavigateNext && (
								<button
									className="statistics-chart__nav statistics-chart__nav--next"
									onClick={handleNext}
									aria-label="Next period">
									<i className="icons-alt-arrow-right" />
								</button>
							)}
						</>
					)}
					<ResponsiveContainer
						width="100%"
						height="100%">
						{renderChart()}
					</ResponsiveContainer>
				</div>
			</div>
		);
	}
);

StatisticsChart.displayName = 'StatisticsChart';
